/**
 * Minimal dependency-free ZIP writer (STORE method, no compression).
 *
 * Produces a valid .zip for the export bundle on web. We deliberately avoid a
 * heavy zip dependency for the bundle path; the marketplace sheet (CSV/XLSX)
 * uses its own writer. STORE-only keeps this small and deterministic.
 */

interface ZipEntry {
  name: string;
  bytes: Uint8Array;
}

/** Hard limits of the classic (non-ZIP64) format. Exceeding any of them silently WRAPPED the
 *  16/32-bit header fields and produced a corrupt archive that reported success, so we refuse
 *  loudly instead. Adding ZIP64 is the fix if a bundle ever legitimately needs to be bigger. */
const MAX_ENTRIES = 0xffff; // 65535 — the EOCD entry counters are u16
const MAX_U32 = 0xffffffff; // 4 GiB - 1 — sizes and offsets are u32

/** General-purpose bit 11: the file name is UTF-8. Without it Windows/Explorer decodes names
 *  as CP437, so any non-ASCII design name ("Zoë.png", CJK, emoji) unzipped as mojibake. */
const FLAG_UTF8 = 0x0800;

// CRC-32 (standard polynomial), table-driven.
const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    table[n] = c >>> 0;
  }
  return table;
})();

function crc32(bytes: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

function writeU16(arr: number[], v: number): void {
  arr.push(v & 0xff, (v >>> 8) & 0xff);
}
function writeU32(arr: number[], v: number): void {
  arr.push(v & 0xff, (v >>> 8) & 0xff, (v >>> 16) & 0xff, (v >>> 24) & 0xff);
}

export function createZip(entries: ZipEntry[]): Uint8Array {
  const enc = new TextEncoder();
  if (entries.length > MAX_ENTRIES) {
    throw new Error(
      `Zip export supports at most ${MAX_ENTRIES} files (got ${entries.length}). ` +
        `Split the export into smaller batches.`,
    );
  }
  // Ordered output pieces. File BYTES ride as their own zero-copy Uint8Array chunk — never
  // pushed byte-by-byte into a JS number[] and then spread (`Uint8Array.from([...huge])`),
  // which allocated ~16 B/byte on the UI thread AND could overflow the argument-count limit
  // on a big bundle. Headers stay small number[] (converted to a chunk per entry).
  const chunks: Uint8Array[] = [];
  const central: number[] = [];
  let offset = 0;

  for (const entry of entries) {
    const nameBytes = enc.encode(entry.name);
    const size = entry.bytes.length;
    // Limits are checked BEFORE the CRC pass — no point walking gigabytes we can't store.
    if (size > MAX_U32) {
      throw new Error(
        `Zip export can't hold a file larger than 4 GiB ("${entry.name}" is ${size} bytes). ` +
          `ZIP64 is not implemented.`,
      );
    }
    if (offset > MAX_U32) {
      throw new Error(
        `Zip export exceeded the 4 GiB archive limit at "${entry.name}". ` +
          `ZIP64 is not implemented — split the export into smaller batches.`,
      );
    }
    const crc = crc32(entry.bytes);

    // Local file header (small) — the file bytes follow as a separate chunk.
    const local: number[] = [];
    writeU32(local, 0x04034b50);
    writeU16(local, 20); // version needed
    writeU16(local, FLAG_UTF8); // flags — bit 11 = the name below is UTF-8
    writeU16(local, 0); // method = STORE
    writeU16(local, 0); // mod time
    writeU16(local, 0); // mod date
    writeU32(local, crc);
    writeU32(local, size);
    writeU32(local, size);
    writeU16(local, nameBytes.length);
    writeU16(local, 0); // extra len
    for (const b of nameBytes) local.push(b);
    const localHeader = Uint8Array.from(local);
    chunks.push(localHeader, entry.bytes);

    // Central directory header
    writeU32(central, 0x02014b50);
    writeU16(central, 20); // version made by
    writeU16(central, 20); // version needed
    writeU16(central, FLAG_UTF8); // flags — must MATCH the local header's UTF-8 bit
    writeU16(central, 0); // method STORE
    writeU16(central, 0);
    writeU16(central, 0);
    writeU32(central, crc);
    writeU32(central, size);
    writeU32(central, size);
    writeU16(central, nameBytes.length);
    writeU16(central, 0);
    writeU16(central, 0);
    writeU16(central, 0);
    writeU16(central, 0);
    writeU32(central, 0);
    writeU32(central, offset);
    for (const b of nameBytes) central.push(b);

    offset += localHeader.length + entry.bytes.length;
  }

  const centralOffset = offset;
  if (centralOffset > MAX_U32 || central.length > MAX_U32) {
    throw new Error(
      "Zip export exceeded the 4 GiB archive limit. ZIP64 is not implemented — " +
        "split the export into smaller batches.",
    );
  }
  const end: number[] = [];
  writeU32(end, 0x06054b50);
  writeU16(end, 0);
  writeU16(end, 0);
  writeU16(end, entries.length);
  writeU16(end, entries.length);
  writeU32(end, central.length);
  writeU32(end, centralOffset);
  writeU16(end, 0);

  chunks.push(Uint8Array.from(central), Uint8Array.from(end));

  // Single allocation of the exact total, each chunk copied in order (file bytes included
  // once). No number[] of file bytes, no spread of a huge array.
  const total = chunks.reduce((n, c) => n + c.length, 0);
  const out = new Uint8Array(total);
  let p = 0;
  for (const c of chunks) {
    out.set(c, p);
    p += c.length;
  }
  return out;
}
